import { call, put, select, takeLatest } from 'redux-saga/effects';
import axios from 'axios';
import { v4 as uuid } from 'uuid';
import queryString from 'query-string';
import { PAYMENT_METHOD_ACTION } from '~redux/reducers/paymentMethod';
import { AVAILABLE_PAYMENT_METHODS_SET } from '~redux/reducers/availablePaymentMethods';
import { ERROR_ACTIONS } from '~redux/reducers/error';
import { getRequest } from '~redux/selectors';
import { buildHeaders } from './confirm';
import { getAirwayUrl, sendRequestLog } from '~components/utils';
import { POST_EVENTS, sendEvent, ExtendElementType } from '~components/events';
import { ConfirmDataType } from './confirm';

export const buildPaymentMethodBody = async (data: ConfirmDataType) => {
  const { cardnumber, cardname, expiry, cvc, billing } = data;
  // To format expiry from different input field
  const formatExpiry = (expiry || '').replace(' / ', '');
  return {
    type: 'card',
    card: {
      number: cardnumber?.replace(/\s/g, ''),
      cvc,
      expiry_month: formatExpiry.slice(0, 2),
      expiry_year: `20${formatExpiry.slice(2, 4)}`, // TBD update this when we release on 2080, LOL😇
      name: cardname,
    },
    billing,
  };
};

export interface CreatePaymentMethodPayload extends ConfirmDataType {
  customer_id: string;
  metadata: { [key: string]: string };
  customer_client_secret: string;
}

export function* createPaymentMethod(data: { type: string; payload: CreatePaymentMethodPayload }) {
  yield put({ type: ERROR_ACTIONS.SET, payload: {} });
  const request = yield select(getRequest);
  const { customer_id, billing, metadata, componentName } = data.payload || {};
  try {
    const reqPath = `${getAirwayUrl()}/payment_methods/create`;
    const reqPayload = {
      request_id: uuid(),
      ...(yield buildPaymentMethodBody(data.payload)),
      customer_id,
      billing,
      metadata,
    };
    const reqHeader = buildHeaders(request);
    sendRequestLog({ componentName, reqPath, reqHeader, reqPayload, origin: request.origin });
    const { data: paymentMethod } = yield call([axios, 'post'], reqPath, reqPayload, reqHeader);
    sendEvent(
      {
        type: componentName,
        code: POST_EVENTS.onLogResponse,
        path: reqPath,
        res: paymentMethod,
        header: {
          ...reqHeader,
          'Awx-Tracker': null,
        },
      },
      request.origin,
    );
    yield put({ type: PAYMENT_METHOD_ACTION, payload: paymentMethod });
    componentName &&
      sendEvent(
        {
          type: componentName,
          code: POST_EVENTS.onPaymentMethodCreated,
          paymentMethod: paymentMethod,
        },
        request.origin,
      );
    return paymentMethod.id;
  } catch (error) {
    yield put({ type: ERROR_ACTIONS.SET, payload: error?.response?.data });
    componentName &&
      sendEvent(
        {
          type: componentName,
          code: POST_EVENTS.onPaymentMethodCreatedError,
          error: error?.response?.data,
        },
        request.origin,
      );
  }
}

type GetAvailablePaymentMethodsPayload = {
  componentName: ExtendElementType;
  transaction_currency: string;
  transaction_mode?: 'oneoff' | 'recurring';
};

export function* getAvailablePaymentMethods({ payload }: { type: string; payload: GetAvailablePaymentMethodsPayload }) {
  const { componentName, ...rest } = payload;
  const request = yield select(getRequest);
  try {
    const reqPath = `${getAirwayUrl()}/config/payment_method_types`;
    const reqPayload = {
      page_num: 0,
      page_size: 200,
      active: true,
      ...rest,
    };
    const reqHeader = buildHeaders(request);
    sendRequestLog({ componentName, reqPath, reqHeader, reqPayload, origin: request.origin });
    const { data } = yield call([axios, 'get'], `${reqPath}?${queryString.stringify(reqPayload)}`, reqHeader);
    sendEvent(
      {
        type: 'hpp',
        code: POST_EVENTS.onLogResponse,
        path: reqPath,
        res: data,
        header: {
          ...reqHeader,
          'Awx-Tracker': null,
        },
      },
      request.origin,
    );
    yield put({ type: AVAILABLE_PAYMENT_METHODS_SET, payload: data.items });
  } catch (err) {
    yield put({ type: ERROR_ACTIONS.SET, payload: err?.response?.data });
  }
}

export const AWX_GET_AVAILABLE_PAYMENT_METHODS_ASYNC = 'AWX_GET_AVAILABLE_PAYMENT_METHODS_ASYNC';

export const AWX_CREATE_PAYMENT_METHOD_ASYNC = 'AWX_CREATE_PAYMENT_METHOD_ASYNC';

export function* watchGetAvailablePaymentMethods() {
  yield takeLatest(AWX_GET_AVAILABLE_PAYMENT_METHODS_ASYNC, getAvailablePaymentMethods);
}

export function* watchCreatePaymentMethodAsync() {
  yield takeLatest(AWX_CREATE_PAYMENT_METHOD_ASYNC, createPaymentMethod);
}
